/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

const ua = window.navigator.userAgent;
// Add Chrome detection
const chrome = ua.indexOf("Chrome/");
const msie = ua.indexOf("MSIE ");
const msie11 = ua.indexOf("Trident/");
const msedge = ua.indexOf("Edge/");
const firefox = ua.indexOf("Firefox");
const isIE = msie > 0 || msie11 > 0;
const isEdge = msedge > 0;
const isFirefox = firefox > 0; // Only needed if you need to support the redirect flow in Firefox incognito
const isChrome = chrome > 0 && ua.indexOf("Chromium") === -1; // Exclude Chromium as it's also detected as 

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: 'B2C_1_susi_v2',
        forgotPassword: 'B2C_1_reset_v3',
        editProfile: 'B2C_1_edit_profile_v2',
    },
    authorities: {
        signUpSignIn: {
            authority: 'https://ideacoreb2c.b2clogin.com/ideacoreb2c.onmicrosoft.com/b2c_1_susi_v2',
        },
        forgotPassword: {
            authority: 'https://ideacoreb2c.b2clogin.com/ideacoreb2c.onmicrosoft.com/B2C_1_reset_v3',
        },
        editProfile: {
            authority: 'https://ideacoreb2c.b2clogin.com/ideacoreb2c.onmicrosoft.com/B2C_1_edit_profile_v2',
        },
    },
    authorityDomain: 'ideacoreb2c.b2clogin.com',
};


/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: 'd6b294da-d4d4-486a-bd9b-ac32ad8bee99', // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: isIE || isEdge || isFirefox // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    apiTodoList: {
        //endpoint: 'http://localhost:5001/api/todolist',
        //endpoint: 'http://backend:5001/api/todolist',
        //endpoint: 'https://react-b2c-api-backend.mangoforest-e9c315be.centralus.azurecontainerapps.io/api/todolist',
        endpoint: 'https://ideanodejsapi.azurewebsites.net/api/todolist',
        scopes: {
            read: ['https://ideacoreb2c.onmicrosoft.com/fd0129dc-ac6b-4dee-8df0-41b9f9179159/user.read'],
            write: ['https://ideacoreb2c.onmicrosoft.com/fd0129dc-ac6b-4dee-8df0-41b9f9179159/todolist.readwrite'],
        },
    },
    apiCustomerList: {
        //endpoint: 'http://localhost:5001/api/todolist',
        //endpoint: 'http://backend:5001/api/todolist',
        //endpoint: 'https://react-b2c-api-backend.mangoforest-e9c315be.centralus.azurecontainerapps.io/api/customerlist',
        endpoint: 'https://ideanodejsapi.azurewebsites.net/api/customerlist',
        scopes: {
            read: ['https://ideacoreb2c.onmicrosoft.com/fd0129dc-ac6b-4dee-8df0-41b9f9179159/user.read'],
            write: ['https://ideacoreb2c.onmicrosoft.com/fd0129dc-ac6b-4dee-8df0-41b9f9179159/todolist.readwrite'],
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...protectedResources.apiTodoList.scopes.read, ...protectedResources.apiTodoList.scopes.write],
};
